import { ButtonPrimary, ButtonTertiary } from "components/Atoms/Button";
import React, { useCallback, useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader, useOpenPreviewPanel } from "../PreviewPanel";
import { ShowPreviewPanelContent } from "./ShowPreviewPanelContent";

interface ShowPreviewPanelProps {
  showId: number
  onChange: () => void
  canEdit: boolean
}

const ShowPreviewPanel = (props: ShowPreviewPanelProps) => {
  const { showId, onChange, canEdit } = props

  const api = useApi(() => window.CoreApi.Show.GetPreviewPanelData(showId), ["show", showId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [showId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={site.currentEnvironment.show.singular} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const data = api.data;
  const products = data.products;
  const show = data.show;
  const exclusivityClause = data.exclusivityClause;

  const isInternalUser = site.currentEnvironment.type === "internal";

  const costsExTax = products.subtotal.costs != null
    ? products.subtotal.costs.amount
    : null;

  const salesExTax = products.subtotal.sales != null
    ? products.subtotal.sales.amount
    : null;

  const profitExTax = (costsExTax != null && salesExTax != null)
    ? salesExTax - costsExTax
    : null;

  const hasProducts = products.collection.length > 0;
  const displayFinancials = hasProducts && isInternalUser && (costsExTax != null || salesExTax != null);

  return <>
    <PreviewPanelHeader title={site.currentEnvironment.show.singular} />
    <ShowPreviewPanelContent
      show={show}
      exclusivityClause={exclusivityClause}
      isInternalUser={isInternalUser}
      freeFieldCategories={data.freeFieldCategories}
      freeFieldPermissions={data.freeFieldDefinitionPermissions}
      displayFinancials={displayFinancials}
      salesExTax={salesExTax}
      costsExTax={costsExTax}
      profitExTax={profitExTax}
      onChange={onChange}
      canEdit={canEdit}
    />
    <PreviewPanelFooter>
      <ButtonPrimary href={routes.show_Detail(showId).absolute} label={RESX.Buttons.DetailView} />
      {show.permissions.canModify && <ButtonTertiary href={routes.show_Edit(showId).absolute} label={RESX.Buttons.btnEdit} />}
    </PreviewPanelFooter>
  </>
}

export interface ShowPreviewProps {
  showId: number
  trigger: JSX.Element
  onOpen?: () => void
  onChange: () => void
  canEdit: boolean
}

export const ShowPreview = (props: ShowPreviewProps) => {
  return <PreviewPanel
    onOpen={props.onOpen}
    trigger={props.trigger}
    size="medium"
    id={`show-preview-${props.showId}`}
  >
    <ShowPreviewPanel showId={props.showId} onChange={props.onChange} canEdit={props.canEdit} />
  </PreviewPanel>
};

export const useOpenShowPreviewPanel = () => {
  const openPreviewPanel = useOpenPreviewPanel();

  const openShowPreviewPanel = useCallback((showId: number) => {
    openPreviewPanel(<ShowPreviewPanel
      showId={showId}
      onChange={() => { }}
      canEdit
    />, `show-preview-${showId}`);
  }, [openPreviewPanel]);

  return openShowPreviewPanel;
}